import { useState, useContext, useCallback, useRef, useEffect } from "react";
import { GroupContext } from "../../services/contexts/GroupContext";
import toast from "react-hot-toast";
import Confirmation from "../Confirmation";
import CustomButton from "../common/CustomButton";
import classes from "./GroupModal.module.css";
import { Modal, Form } from "react-bootstrap";
import TreeSelect from "../common/TreeSelect";
import AddUserIcon from "../../svg/AddUsers.svg";
import { truncateText } from "../../helpers/common";

function GroupModal({
  allowedUsers,
  originalGroupName,
  originalGroupId = null,
}) {
  const { createGroup, editGroup, globalData } = useContext(GroupContext);
  const calculateDefaultExcludeUsers = useCallback(() => {
    const removedAllowedUsers =
      allowedUsers && allowedUsers.length > 0
        ? new Set(allowedUsers.map((item) => item.user_id))
        : new Set([]);
    const result = globalData.memberUsers
      .filter((item) => !removedAllowedUsers.has(item.id))
      .map((e) => ({ ...e, user_id: e.id }));
    return result;
  }, [globalData.memberUsers]);

  const creatingNewGroup = originalGroupId === null;

  const confirmRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [groupName, setGroupName] = useState(originalGroupName || "");
  const [message, setMessage] = useState("");

  const [includedUsers, setIncludedUsers] = useState(allowedUsers || []);
  const [excludeUsers, setExcludeUsers] = useState(
    calculateDefaultExcludeUsers() || []
  );

  useEffect(() => {
    if (!isOpen) {
      setGroupName(originalGroupName || "");
      setIncludedUsers(allowedUsers || []);
      setExcludeUsers(calculateDefaultExcludeUsers() || []);
    }
  }, [originalGroupName, allowedUsers, isOpen, calculateDefaultExcludeUsers]);

  const isDirty = () => {
    if (creatingNewGroup) {
      return groupName || groupName.length !== 0;
    } else {
      const fileNameChanged = groupName !== originalGroupName;
      const permissionChanged = () => {
        //The group previously had assigned users.
        if (allowedUsers && allowedUsers.length > 0) {
          return !(
            includedUsers.length === allowedUsers.length &&
            includedUsers.every((value, index) => value === allowedUsers[index])
          );
        } else {
          // No users had assigned to group before.
          return includedUsers && includedUsers.length > 0;
        }
      };
      return fileNameChanged || permissionChanged();
    }
  };
  const handleSaveGroup = async () => {
    let res;
    if (creatingNewGroup) {
      res = await createGroup({ name: groupName, includedUsers });
    } else {
      res = await editGroup({
        id: originalGroupId,
        name: groupName,
        includedUsers,
      });
    }

    if (res.status !== 200 && res.data.msg) {
      setMessage(res.data.msg);
    } else {
      toast.success(`${truncateText(groupName)} saved successfully!`);
      handleClose();
    }
  };
  const handleClose = () => {
    setIsOpen(false);
    setGroupName(originalGroupName || "");
    setMessage("");
    setIncludedUsers(allowedUsers || []);
    setExcludeUsers(calculateDefaultExcludeUsers() || []);
  };

  return (
    <>
      <CustomButton
        variant="outline-primary"
        onClick={() => setIsOpen(true)}
        className={
          creatingNewGroup
            ? classes.create_group_button
            : classes.edit_group_button
        }
      >
        {creatingNewGroup && (
          <img src={AddUserIcon} className={classes.button_icon} width={26} />
        )}
        <span>{creatingNewGroup ? "Add Group" : "Edit"}</span>
      </CustomButton>
      <Modal
        show={isOpen}
        onHide={handleClose}
        dialogClassName={classes.create_modal_dialog}
        backdropClassName={classes.create_modal_backdrop}
        contentClassName={classes.create_modal_content}
        backdrop="static"
      >
        <Modal.Header
          className={`_g-modal-header_ ${classes["group-modal-header"]}`}
        >
          <div className="_g-close-button-anchor_">
            <div className="_g-close-button_" onClick={handleClose}>
              &#x2715;
            </div>
          </div>
          {creatingNewGroup ? "Add Group" : "Edit Group"}
        </Modal.Header>

        <Modal.Body className={`_g-modal-body_ ${classes.create_modal_body}`}>
          <Form
            onSubmit={(e) => {
              e.preventDefault();
              if (isDirty()) {
                confirmRef.current?.firstChild?.click();
              }
            }}
            style={{
              display: "flex",
              gap: "15px",
              flexDirection: "column",
              margin: "15px 0",
            }}
          >
            <Form.Group>
              <Form.Label
                style={{
                  color: "#000",
                  fontSize: "0.75rem",
                  fontStyle: "normal",
                  fontWeight: 400,
                  lineHeight: "normal",
                }}
              >
                Group Name <span style={{ color: "red" }}>*</span>
              </Form.Label>
              <Form.Control
                required
                type="text"
                className={classes["group-name-input"]}
                value={groupName}
                maxLength={255}
                onChange={(e) => {
                  setGroupName(e.target.value);
                  if (e.target.value.length === 255) {
                    toast.error(`Group can not be longer than 255 characters.`);
                  }
                }}
              />
            </Form.Group>
            {message.length > 0 && (
              <div className={classes["error-feedback"]}>{message}</div>
            )}

            <TreeSelect
              includedUsers={includedUsers}
              setIncludedUsers={setIncludedUsers}
              excludeUsers={excludeUsers}
              setExcludeUsers={setExcludeUsers}
            />

            <div className="_g-modal-button-wrapper_">
              <CustomButton
                variant="outline-secondary"
                type="button"
                onClick={handleClose}
              >
                Cancel
              </CustomButton>
              <div ref={confirmRef} style={{ display: "contents" }}>
                <Confirmation
                  content={`Please confirm ${creatingNewGroup ? "creating" : "updating"} this group: ${groupName}? `}
                  disabled={!isDirty()}
                  Children={
                    <CustomButton
                      variant="primary"
                      type="button"
                      disabled={!isDirty()}
                    >
                      Save
                    </CustomButton>
                  }
                  onConfirm={handleSaveGroup}
                />
              </div>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </>
  );
}

export default GroupModal;
